// category-card.tsx
'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ImageType } from '@eliadmin/core/src/api/api-types';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { SubCategoriesEntity } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { CATEGORY_CONFIG } from '@eliadmin/core/src/constants/category-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';
import type { PropsWithChildren, ReactNode } from 'react';
import React from 'react';
import { Heading } from '../ui/heading';

type CategoryCardEntity = CategoriesEntity | SubCategoriesEntity;

type LegacyCategoryCardProps = {
  category: CategoryCardEntity;
  className?: string;
  inCategory?: boolean;
};

export type CategoryCardProps = PropsWithChildren<{
  href?: string;
  noLink?: boolean;
  className?: { root?: string; content?: string } | string;
}>;

function isLegacyCategoryCardProps(
  props: LegacyCategoryCardProps | CategoryCardProps,
): props is LegacyCategoryCardProps {
  return 'category' in props;
}

export function CategoryCard(props: LegacyCategoryCardProps | CategoryCardProps) {
  if (isLegacyCategoryCardProps(props)) {
    const { category, className, inCategory } = props;

    return (
      <Link
        prefetchOnHover
        href={ROUTES.CATEGORY({ slug: category.slug })}
        className={cn(
          'col-span-1 flex w-full flex-col items-center justify-center gap-2.5 rounded-[12px] border border-border p-4 transition-all hover:bg-border/20',
          className,
        )}
      >
        {/* Most subcategories have no icon; hiding them left the category header without links. */}
        {!!category.icon?.url && (
          <Image
            src={category.icon.url}
            alt={resolveImageAlt(category.icon, category.name)}
            width={inCategory ? 48 : 24}
            height={inCategory ? 48 : 24}
            className="shrink-0 object-contain"
          />
        )}
        <Heading tag="h2" styleAs="h6" title={category.name} center />
      </Link>
    );
  }

  const rootClassName =
    typeof props.className === 'string' ? props.className : props.className?.root;
  const contentClassName =
    typeof props.className === 'string' ? undefined : props.className?.content;
  const content = (
    <div
      className={cn(
        'line-clamp-2 flex h-full w-full flex-row items-center gap-4 text-ellipsis p-3',
        'ea-category-card__content',
        contentClassName,
      )}
    >
      {props.children}
    </div>
  );

  if (props.noLink) {
    return (
      <div
        className={cn(
          'h-[56px] rounded-[12px] border border-border transition-all hover:bg-border/20',
          'ea-category-card',
          rootClassName,
        )}
      >
        {content}
      </div>
    );
  }

  return (
    <Link
      href={props.href ?? '#'}
      className={cn(
        'block h-[56px] rounded-[12px] border border-border transition-all hover:bg-border/20',
        'ea-category-card ea-category-card__link',
        rootClassName,
      )}
      prefetchOnHover
    >
      {content}
    </Link>
  );
}

export function CategoryCardImage(props: {
  url: string;
  alt?: string;
  image?: ImageType;
  className?: { root?: string; image?: string };
}) {
  return (
    <div className={cn('flex-shrink-0', 'ea-category-card-image__root', props.className?.root)}>
      <Image
        alt={resolveImageAlt(props?.url ? props : props?.image, 'category-image')}
        src={props.url || props.image?.url || '/images/placeholder.png'}
        width={32}
        height={32}
        className={cn(
          'h-8 w-8 object-contain',
          'ea-category-card-image__image',
          props.className?.image,
        )}
      />
    </div>
  );
}

export function CategoryCardName(props: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn('text-left text-sm', 'ea-category-card-name', props.className)}>
      <span>{props.children}</span>
    </div>
  );
}

export function CategoryCardDescription(props: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn('pt-4 text-left text-xs', 'ea-category-card-description', props.className)}>
      <span>{props.children}</span>
    </div>
  );
}

export function CategoryCardArrow(props: { className?: string; icon?: ReactNode }) {
  return (
    <div
      className={cn(
        'flex flex-1 justify-end text-left text-foreground',
        'ea-category-card-arrow',
        props.className,
      )}
    >
      {React.cloneElement((props.icon || CATEGORY_CONFIG.categoryCard.arrowIcon) as any, {
        className: cn('h-4 w-4', 'ea-category-card-arrow__icon'),
      })}
    </div>
  );
}
